import React from "react";
import "./SiraguLogo.css";

/* ── Siragu brand mark ────────────────────────────────────────────────────
   A symmetric front-view rainbow butterfly (per the approved reference):
   warm wings on the left (yellow/orange → red/pink), cool wings on the right
   (purple/blue → blue/cyan), a dark segmented body with curling antennae, and
   glossy white highlight dots on the upper wings. Pure SVG + CSS.

   Props: size, withWordmark, active/listening (wings flap while listening).
   Respects prefers-reduced-motion.
   ───────────────────────────────────────────────────────────────────────── */
function SiraguLogo({
  size = 32,
  withWordmark = false,
  className = "",
  active = false,
  listening = false,
}) {
  const isActive = active || listening;
  const uid = React.useMemo(() => `sg-${Math.random().toString(36).slice(2, 7)}`, []);

  const mark = (
    <span
      className={`siragu-logo-mark${isActive ? " is-listening" : ""} ${className}`}
      style={{ width: size, height: size }}
      role="img"
      aria-label={isActive ? "Siragu — listening" : "Siragu"}
    >
      <svg viewBox="0 0 64 64" width={size} height={size} fill="none" aria-hidden="true" className="siragu-mark-svg">
        <defs>
          <linearGradient id={`${uid}-ul`} x1="6" y1="10" x2="32" y2="30" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#FBBF24" /><stop offset="0.5" stopColor="#F97316" /><stop offset="1" stopColor="#EC4899" />
          </linearGradient>
          <linearGradient id={`${uid}-ur`} x1="58" y1="10" x2="32" y2="30" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#8B5CF6" /><stop offset="0.5" stopColor="#6366F1" /><stop offset="1" stopColor="#22D3EE" />
          </linearGradient>
          <linearGradient id={`${uid}-ll`} x1="9" y1="54" x2="32" y2="34" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#EF4444" /><stop offset="0.55" stopColor="#EC4899" /><stop offset="1" stopColor="#F472B6" />
          </linearGradient>
          <linearGradient id={`${uid}-lr`} x1="55" y1="54" x2="32" y2="34" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#22D3EE" /><stop offset="0.55" stopColor="#3B82F6" /><stop offset="1" stopColor="#818CF8" />
          </linearGradient>
        </defs>

        {/* Left wings (warm) */}
        <g className="siragu-wing siragu-wing-left">
          <path d="M32 26 C 24 17, 12 11, 6 12 C 1 13, 1 22, 6 28 C 12 34, 25 33, 32 30 Z" fill={`url(#${uid}-ul)`} />
          <path d="M32 32 C 24 34, 12 38, 9 48 C 7 55, 15 56, 23 51 C 29 47, 31 41, 32 34 Z" fill={`url(#${uid}-ll)`} />
          {/* gloss dots */}
          <circle cx="12" cy="16" r="1.1" fill="#fff" opacity="0.85" />
          <circle cx="16" cy="15" r="0.9" fill="#fff" opacity="0.7" />
          <ellipse cx="13" cy="20" rx="3" ry="1.6" fill="#fff" opacity="0.28" />
        </g>

        {/* Right wings (cool) */}
        <g className="siragu-wing siragu-wing-right">
          <path d="M32 26 C 40 17, 52 11, 58 12 C 63 13, 63 22, 58 28 C 52 34, 39 33, 32 30 Z" fill={`url(#${uid}-ur)`} />
          <path d="M32 32 C 40 34, 52 38, 55 48 C 57 55, 49 56, 41 51 C 35 47, 33 41, 32 34 Z" fill={`url(#${uid}-lr)`} />
          <circle cx="52" cy="16" r="1.1" fill="#fff" opacity="0.85" />
          <circle cx="48" cy="15" r="0.9" fill="#fff" opacity="0.7" />
          <ellipse cx="51" cy="20" rx="3" ry="1.6" fill="#fff" opacity="0.28" />
        </g>

        {/* Body + head */}
        <ellipse cx="32" cy="33" rx="2.3" ry="12" fill="#1E1B4B" />
        <circle cx="32" cy="19" r="2.7" fill="#1E1B4B" />
        {/* abdomen segments */}
        <g stroke="#4338CA" strokeWidth="0.7" opacity="0.6">
          <line x1="30.2" y1="30" x2="33.8" y2="30" /><line x1="30.2" y1="34" x2="33.8" y2="34" />
          <line x1="30.4" y1="38" x2="33.6" y2="38" /><line x1="30.8" y1="42" x2="33.2" y2="42" />
        </g>

        {/* Antennae */}
        <g stroke="#1E1B4B" strokeWidth="1.1" strokeLinecap="round" fill="none">
          <path d="M31 18 C 28 12, 26 9, 24.5 8" />
          <path d="M33 18 C 36 12, 38 9, 39.5 8" />
        </g>
        <circle cx="24.3" cy="7.6" r="1" fill="#1E1B4B" />
        <circle cx="39.7" cy="7.6" r="1" fill="#1E1B4B" />
      </svg>
    </span>
  );

  if (!withWordmark) return mark;

  return (
    <span className="siragu-logo-lockup">
      {mark}
      <span className="siragu-wordmark">Siragu</span>
    </span>
  );
}

export default SiraguLogo;
